import { IconButton } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownloadTwoTone';
import { GridCsvGetRowsToExportParams, gridFilteredSortedRowIdsSelector, useGridApiContext } from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { useAppSelector } from '@reduxStore';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';
import { ExportActionProps } from '@components/DataGrid/Dispatch/types';
import useDebouncer from '@hooks/useDebounce';

import styles from '../DataGrid.Dispatch.module.css';

/**
 * Button for triggering CSV download of the currently visible legs on the Dispatch/Research Board.
 */
export default function ExportGridButton({ name, disabled }: ExportActionProps) {
	const gridContext = useGridApiContext();
	const { selectedTerminal } = useAppSelector((state) => state.applicationState);
	const hiddenAnchorRef = useRef<HTMLAnchorElement>(null);
	// Preventing button clicking spam
	const [downloading, setDownloading] = useState(false);

	const handleExport = () => {
		if (!downloading && hiddenAnchorRef.current) {
			setDownloading(true);

			// Get the visible grid data in CSV format
			const csvData = gridContext.current
				.getDataAsCsv({
					getRowsToExport: ({ apiRef }: GridCsvGetRowsToExportParams) => gridFilteredSortedRowIdsSelector(apiRef)
				})
				.replaceAll('|||', ',')
				.replaceAll('undefined', '');

			// Create blob of the data
			const csvBlob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csvData], {
				type: 'text/csv;charset=utf-8,'
			});
			// Create a url of the blob
			const csvUrl = URL.createObjectURL(csvBlob);

			const filenameBase = [String(selectedTerminal?.number), name.toUpperCase(), dayjsNow().format('YYYY-MM-DD_HHmmss')].join('-');

			// Use the hidden anchor to download the blob
			hiddenAnchorRef.current.download = `${filenameBase}.csv`;
			hiddenAnchorRef.current.href = csvUrl;
			hiddenAnchorRef.current.click();
		}
	};

	useDebouncer(
		() => {
			if (downloading) {
				setDownloading(false);
			}
		},
		[downloading],
		800
	);

	return (
		<>
			<IconButton
				title={`Export ${name}`}
				onClick={handleExport}
				disabled={disabled}
				className={`${styles['icon-btn']} ${styles['export-btn']} ${downloading ? styles.animate : ''}`}
			>
				<FileDownloadIcon />
			</IconButton>
			<a
				style={{ display: 'none' }}
				target='_blank'
				ref={hiddenAnchorRef}
			>
				Hidden Anchor
			</a>
		</>
	);
}
